// components/Providers/OptimizedProviderForm.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import SmtpTemplateManager from './SmtpTemplateManager';

const OptimizedProviderForm = ({ onProviderCreated, onClose }) => {
  const [step, setStep] = useState(1); // 1: Template, 2: Provider details, 3: Bulk create
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [mode, setMode] = useState('single'); // 'single' ou 'bulk'
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('prae.jwt');

  // Formulaire pour provider unique
  const [providerForm, setProviderForm] = useState({
    name: '',
    from_name: '',
    from_email: '',
    domain: '',
    provider: '',
    bounce_domain: '',
    dkim_selector: '',
  });

  // Formulaire pour création en masse
  const [bulkForm, setBulkForm] = useState({
    providers: [
      {
        name: '',
        from_name: '',
        from_email: '',
        domain: '',
        provider: '',
        bounce_domain: '',
        dkim_selector: '',
      },
    ],
  });

  const handleTemplateSelect = template => {
    setSelectedTemplate(template);
    setStep(2);
  };

  const generateDkimSelector = () => {
    return Math.random().toString(36).substring(2, 10);
  };

  const addBulkProvider = () => {
    setBulkForm(prev => ({
      ...prev,
      providers: [
        ...prev.providers,
        {
          name: '',
          from_name: '',
          from_email: '',
          domain: '',
          provider: '',
          bounce_domain: '',
          dkim_selector: generateDkimSelector(),
        },
      ],
    }));
  };

  const removeBulkProvider = index => {
    setBulkForm(prev => ({
      ...prev,
      providers: prev.providers.filter((_, i) => i !== index),
    }));
  };

  const updateBulkProvider = (index, field, value) => {
    setBulkForm(prev => ({
      ...prev,
      providers: prev.providers.map((provider, i) =>
        i === index ? { ...provider, [field]: value } : provider
      ),
    }));
  };

  // Auto-génération des champs basés sur le domaine
  const autofillFromDomain = (domain, index = null) => {
    if (!domain) return;

    const subdomain = domain.split('.')[0];
    const autoFields = {
      provider: domain,
      bounce_domain: `b.${domain}`,
      dkim_selector: generateDkimSelector(),
    };

    if (mode === 'single') {
      setProviderForm(prev => ({ ...prev, ...autoFields }));
    } else if (index !== null) {
      updateBulkProvider(index, 'provider', autoFields.provider);
      updateBulkProvider(index, 'bounce_domain', autoFields.bounce_domain);
      updateBulkProvider(index, 'dkim_selector', autoFields.dkim_selector);
    }
  };

  const handleSingleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/smtp-providers/from-template`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            templateId: selectedTemplate.id,
            providerData: providerForm,
          }),
        }
      );

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Erreur création provider');
      }

      const data = await res.json();
      toast.success('Provider créé avec succès !');
      onProviderCreated?.(data.provider);
      onClose?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const validProviders = bulkForm.providers.filter(
        p => p.name && p.from_email && p.domain
      );

      if (validProviders.length === 0) {
        throw new Error('Aucun provider valide à créer');
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/smtp-providers/bulk-from-template`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            templateId: selectedTemplate.id,
            providers: validProviders,
          }),
        }
      );

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Erreur création providers');
      }

      const data = await res.json();
      toast.success(`${data.providers.length} providers créés avec succès !`);
      onProviderCreated?.(data.providers);
      onClose?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedTemplate(null);
    setMode('single');
    setProviderForm({
      name: '',
      from_name: '',
      from_email: '',
      domain: '',
      provider: '',
      bounce_domain: '',
      dkim_selector: '',
    });
    setBulkForm({
      providers: [
        {
          name: '',
          from_name: '',
          from_email: '',
          domain: '',
          provider: '',
          bounce_domain: '',
          dkim_selector: '',
        },
      ],
    });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header avec étapes */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Créer des Providers SMTP</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="flex items-center space-x-4">
          <div
            className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                step >= 1
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-gray-300'
              }`}
            >
              1
            </div>
            <span className="ml-2">Template</span>
          </div>
          <div className="w-8 h-px bg-gray-300"></div>
          <div
            className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                step >= 2
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-gray-300'
              }`}
            >
              2
            </div>
            <span className="ml-2">Configuration</span>
          </div>
        </div>
      </div>

      {/* Étape 1: Sélection du template */}
      {step === 1 && (
        <SmtpTemplateManager onTemplateSelect={handleTemplateSelect} />
      )}

      {/* Étape 2: Configuration des providers */}
      {step === 2 && selectedTemplate && (
        <div className="space-y-6">
          {/* Informations du template sélectionné */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">
              Template sélectionné: {selectedTemplate.name}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <strong>Host:</strong> {selectedTemplate.host}
              </div>
              <div>
                <strong>Server:</strong> {selectedTemplate.server}
              </div>
              <div>
                <strong>IP:</strong> {selectedTemplate.outbound_ip}
              </div>
              <div>
                <strong>Zone:</strong> {selectedTemplate.zone}
              </div>
            </div>
          </div>

          {/* Sélecteur de mode */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Mode de création</h3>
            <div className="flex gap-4">
              <button
                onClick={() => setMode('single')}
                className={`px-4 py-2 rounded border ${
                  mode === 'single'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Provider unique
              </button>
              <button
                onClick={() => setMode('bulk')}
                className={`px-4 py-2 rounded border ${
                  mode === 'bulk'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Création en masse
              </button>
            </div>
          </div>

          {/* Formulaire provider unique */}
          {mode === 'single' && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Nouveau Provider</h3>
              <form
                onSubmit={handleSingleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Nom du provider *
                  </label>
                  <input
                    required
                    value={providerForm.name}
                    onChange={e =>
                      setProviderForm(prev => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-3 py-2"
                    placeholder="ex: kmr_linkedclean.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    From Name *
                  </label>
                  <input
                    required
                    value={providerForm.from_name}
                    onChange={e =>
                      setProviderForm(prev => ({
                        ...prev,
                        from_name: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-3 py-2"
                    placeholder="ex: KMR Nettoyage"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    From Email *
                  </label>
                  <input
                    required
                    type="email"
                    value={providerForm.from_email}
                    onChange={e =>
                      setProviderForm(prev => ({
                        ...prev,
                        from_email: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-3 py-2"
                    placeholder="ex: bonjour@kmr.linkedclean.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Domain *
                  </label>
                  <input
                    required
                    value={providerForm.domain}
                    onChange={e => {
                      const value = e.target.value;
                      setProviderForm(prev => ({ ...prev, domain: value }));
                      autofillFromDomain(value);
                    }}
                    className="w-full border rounded px-3 py-2"
                    placeholder="ex: kmr.linkedclean.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Provider
                  </label>
                  <input
                    value={providerForm.provider}
                    onChange={e =>
                      setProviderForm(prev => ({
                        ...prev,
                        provider: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-3 py-2 bg-gray-50"
                    placeholder="Auto-généré"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Bounce Domain
                  </label>
                  <input
                    value={providerForm.bounce_domain}
                    onChange={e =>
                      setProviderForm(prev => ({
                        ...prev,
                        bounce_domain: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-3 py-2 bg-gray-50"
                    placeholder="Auto-généré"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    DKIM Selector
                  </label>
                  <div className="flex gap-2">
                    <input
                      value={providerForm.dkim_selector}
                      onChange={e =>
                        setProviderForm(prev => ({
                          ...prev,
                          dkim_selector: e.target.value,
                        }))
                      }
                      className="flex-1 border rounded px-3 py-2 bg-gray-50"
                      placeholder="Auto-généré"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setProviderForm(prev => ({
                          ...prev,
                          dkim_selector: generateDkimSelector(),
                        }))
                      }
                      className="bg-gray-500 text-white px-3 py-2 rounded hover:bg-gray-600"
                    >
                      Générer
                    </button>
                  </div>
                </div>

                <div className="md:col-span-2 flex gap-2 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Création...' : 'Créer Provider'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
                  >
                    Retour
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Formulaire création en masse */}
          {mode === 'bulk' && (
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Création en masse</h3>
                <button
                  onClick={addBulkProvider}
                  className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700"
                >
                  + Ajouter un provider
                </button>
              </div>

              <form onSubmit={handleBulkSubmit} className="space-y-4">
                {bulkForm.providers.map((provider, index) => (
                  <div key={index} className="border rounded p-4 relative">
                    {bulkForm.providers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBulkProvider(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    )}

                    <h4 className="font-medium mb-3">Provider #{index + 1}</h4>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Nom *
                        </label>
                        <input
                          required
                          value={provider.name}
                          onChange={e =>
                            updateBulkProvider(index, 'name', e.target.value)
                          }
                          className="w-full border rounded px-3 py-2"
                          placeholder="Nom provider"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          From Name *
                        </label>
                        <input
                          required
                          value={provider.from_name}
                          onChange={e =>
                            updateBulkProvider(
                              index,
                              'from_name',
                              e.target.value
                            )
                          }
                          className="w-full border rounded px-3 py-2"
                          placeholder="Nom expéditeur"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          From Email *
                        </label>
                        <input
                          required
                          type="email"
                          value={provider.from_email}
                          onChange={e =>
                            updateBulkProvider(
                              index,
                              'from_email',
                              e.target.value
                            )
                          }
                          className="w-full border rounded px-3 py-2"
                          placeholder="email@domain.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Domain *
                        </label>
                        <input
                          required
                          value={provider.domain}
                          onChange={e => {
                            const value = e.target.value;
                            updateBulkProvider(index, 'domain', value);
                            autofillFromDomain(value, index);
                          }}
                          className="w-full border rounded px-3 py-2"
                          placeholder="domain.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Provider
                        </label>
                        <input
                          value={provider.provider}
                          onChange={e =>
                            updateBulkProvider(
                              index,
                              'provider',
                              e.target.value
                            )
                          }
                          className="w-full border rounded px-3 py-2 bg-gray-50"
                          placeholder="Auto-généré"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          DKIM Selector
                        </label>
                        <div className="flex gap-1">
                          <input
                            value={provider.dkim_selector}
                            onChange={e =>
                              updateBulkProvider(
                                index,
                                'dkim_selector',
                                e.target.value
                              )
                            }
                            className="flex-1 border rounded px-3 py-2 bg-gray-50"
                            placeholder="Auto-généré"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              updateBulkProvider(
                                index,
                                'dkim_selector',
                                generateDkimSelector()
                              )
                            }
                            className="bg-gray-500 text-white px-2 py-2 rounded hover:bg-gray-600"
                          >
                            🎲
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex gap-2 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading
                      ? 'Création...'
                      : `Créer ${bulkForm.providers.length} Provider(s)`}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
                  >
                    Retour
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OptimizedProviderForm;
